/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/app/Header";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  EnvelopeSimple, MagnifyingGlass, ArrowSquareIn, PaperPlaneTilt, Paperclip, ArrowsClockwise,
  Link as LinkIcon, FilePlus, X, Calendar, BellRinging,
  ArrowBendUpLeft, ArrowBendDoubleUpLeft, Trash, DotsThreeVertical, Warning,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ComposeEmailDialog from "@/components/app/ComposeEmailDialog";

/**
 * `/inbox` — firm-wide triage of Outlook emails that don't match any case.
 *
 * Each row supports three actions:
 *   1. Attach to existing case (search-as-you-type case picker)
 *   2. Create a new case from this email (pre-fills title from subject)
 *   3. Dismiss (per-user; the email won't reappear in this list)
 *
 * The page is solicitor-only and self-contained — it doesn't share state
 * with the per-case CommunicationsTab beyond the underlying collection.
 */
const CASE_TYPES = [
  { value: "purchase", label: "Purchase" },
  { value: "sale", label: "Sale" },
  { value: "remortgage", label: "Remortgage" },
  { value: "transfer_of_equity", label: "Transfer of Equity" },
  { value: "lease_extension", label: "Lease Extension" },
];

export default function InboxPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState("");
  const [hasAttachments, setHasAttachments] = useState(false);
  // Direction filter: "all" | "inbox" | "sent". Mirrors the per-row pill
  // so a solicitor can focus on either incoming work or sent-but-unfiled.
  const [folderFilter, setFolderFilter] = useState("all");
  const [openMsg, setOpenMsg] = useState(null);
  const [openBody, setOpenBody] = useState({ id: null, html: "", loading: false });
  // Attachment list for the currently-open email. Lazy-loaded when the
  // user opens a row that Graph flagged as `hasAttachments`.
  const [openAttachments, setOpenAttachments] = useState({ id: null, items: [], loading: false });

  // Inbox now has two tabs: "emails" (Outlook triage, the original feature)
  // and "messages" (unread case notes + unreplied inbound emails on cases
  // already on file). Tab + scope persist in localStorage so the solicitor
  // lands back where they left off.
  const [tab, setTab] = useState(() => localStorage.getItem("inbox.tab") || "emails");
  const [scope, setScope] = useState(() => localStorage.getItem("inbox.scope") || "mine");
  const [messages, setMessages] = useState({ unread: [], unreplied: [], counts: { unread: 0, unreplied: 0, total: 0, critical: 0, warning: 0, fresh: 0 } });
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesSubTab, setMessagesSubTab] = useState("all"); // all | unread | unreplied
  // Tracks the row currently being mark-read/mark-replied so the button
  // can show a disabled state without re-rendering the whole list.
  const [busyRowId, setBusyRowId] = useState(null);
  // Progressive disclosure — large mailboxes (2000+ unmatched emails)
  // would otherwise render every row up-front. Page-based pagination at
  // 200 rows per page. Page resets to 1 on filter changes so search,
  // folder, days etc. don't leave the user stuck on page 47.
  const PAGE_SIZE = 200;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [days, search, hasAttachments, folderFilter]);
  // Multi-select for bulk "add to file". Keyed by graph_message_id. Cleared
  // whenever the visible list changes so we never act on rows out of view.
  const [selEmails, setSelEmails] = useState(() => new Set());
  const lastIdxRef = useRef(null);  // anchor row for shift-click range select
  useEffect(() => { setSelEmails(new Set()); lastIdxRef.current = null; }, [days, search, hasAttachments, folderFilter, page, tab]);
  const toggleEmailSel = (id) => setSelEmails((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  // Shift-click selects the whole range between the last-clicked row and this
  // one (faster triage of long runs). A plain click toggles just that row.
  const handleRowSelect = (e, idx, id) => {
    const pageItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    if (e?.shiftKey && lastIdxRef.current !== null) {
      const [a, b] = [lastIdxRef.current, idx].sort((x, y) => x - y);
      const rangeIds = pageItems.slice(a, b + 1).map((m) => m.graph_message_id);
      setSelEmails((s) => { const n = new Set(s); rangeIds.forEach((rid) => n.add(rid)); return n; });
    } else {
      toggleEmailSel(id);
    }
    lastIdxRef.current = idx;
  };
  const bulkDismiss = async () => {
    const ids = Array.from(selEmails);
    if (!ids.length) return;
    try {
      const { data } = await API.post(`/inbox/unmatched/dismiss-bulk`, { graph_message_ids: ids });
      toast.success(`Dismissed ${data.dismissed || ids.length} email${(data.dismissed || ids.length) === 1 ? "" : "s"}`);
      setItems((arr) => arr.filter((m) => !selEmails.has(m.graph_message_id)));
      setSelEmails(new Set());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk dismiss failed");
    }
  };
  useEffect(() => { localStorage.setItem("inbox.tab", tab); }, [tab]);
  useEffect(() => { localStorage.setItem("inbox.scope", scope); }, [scope]);

  // Outlook connection health — fetched once on mount so we can show a
  // "Reconnect Outlook" banner when Microsoft has revoked the refresh
  // token (password change, admin sign-out-everywhere, MFA reset etc.).
  // Without this banner the user has no idea their email sync silently
  // died — they just notice emails stop landing in their case files.
  const [outlookStatus, setOutlookStatus] = useState(null);
  useEffect(() => {
    if (user?.role !== "solicitor") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/outlook/status");
        if (!cancelled) setOutlookStatus(data);
      } catch { /* non-fatal — banner just won't show */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Pull an attachment via the authenticated API client and trigger a
  // browser download. Used by both the inbox triage drawer and the
  // case-communications dialog (the latter goes through CommunicationsTab,
  // but we keep the helper here for symmetry).
  const downloadAttachment = useCallback(async (path, filename) => {
    try {
      const resp = await API.get(path, { responseType: "blob" });
      const blob = new Blob([resp.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "attachment";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Attachment download failed");
    }
  }, []);
  const [action, setAction] = useState(null);  // { msg, mode: "attach"|"newcase" }
  const [composeReply, setComposeReply] = useState(null); // { replyTo, mode: "reply"|"replyAll" }
  const [confirmDelete, setConfirmDelete] = useState(null); // { msg, scope: "outlook"|"local" }
  const [deleting, setDeleting] = useState(false);
  // Set true when the backend returns X-Inbox-Warming on the first load
  // after a fresh Outlook connect. We poll every 5s until the header
  // disappears (cache populated) so emails stream in without the user
  // needing to refresh manually.
  const [warming, setWarming] = useState(false);
  // Tracks Notification API state so we can show an "Enable desktop alerts"
  // prompt the first time a solicitor lands here. `null` = API unsupported.
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : null,
  );
  const [notifDismissed, setNotifDismissed] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem("inbox-notif-prompt-dismissed") === "1",
  );

  const requestNotifPermission = async () => {
    if (typeof Notification === "undefined") return;
    try {
      const res = await Notification.requestPermission();
      setNotifPermission(res);
      if (res === "granted") {
        toast.success("Desktop alerts on — you'll be notified when new emails need triage.");
      } else if (res === "denied") {
        toast.info("Desktop alerts blocked. You can re-enable them in your browser's site settings.");
      }
    } catch {}
  };
  const dismissNotifPrompt = () => {
    try { localStorage.setItem("inbox-notif-prompt-dismissed", "1"); } catch {}
    setNotifDismissed(true);
  };

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && user.role !== "solicitor") {
      toast.error("Solicitor access required");
      navigate("/");
    }
  }, [user, navigate]);

  const fetchUnmatched = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (search.trim()) params.set("search", search.trim());
      if (hasAttachments) params.set("has_attachments", "true");
      if (folderFilter === "inbox" || folderFilter === "sent") {
        params.set("folder", folderFilter);
      }
      const resp = await API.get(`/inbox/unmatched?${params.toString()}`);
      setItems(Array.isArray(resp.data) ? resp.data : []);
      // Backend sets X-Inbox-Warming=1 on the first call after a fresh
      // Outlook connect while it streams the cache from Graph in the
      // background. Frontend then polls every 5s until the header drops.
      setWarming(String(resp.headers?.["x-inbox-warming"] || "") === "1");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load inbox — is your Outlook connected?");
    } finally {
      setLoading(false);
    }
  }, [days, search, hasAttachments, folderFilter]);
  useEffect(() => { if (user?.role === "solicitor") fetchUnmatched(); }, [user, fetchUnmatched]);

  // Messages tab — unread case notes + unreplied inbound emails on cases
  // already on file. Refetches when the scope or tab toggles.
  const fetchMessages = useCallback(async () => {
    setMessagesLoading(true);
    try {
      const { data } = await API.get(`/inbox/messages?scope=${encodeURIComponent(scope)}`);
      setMessages(data || { unread: [], unreplied: [], counts: { unread: 0, unreplied: 0, total: 0, critical: 0, warning: 0, fresh: 0 } });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load messages");
    } finally {
      setMessagesLoading(false);
    }
  }, [scope]);
  // Track which case_ids are already in the critical band so we can fire
  // a desktop notification only when a NEW row crosses the 48-hour
  // threshold (avoids spamming the same case every poll).
  const knownCriticalIdsRef = useRef(new Set());
  // Window focus state — we only ping the desktop when the tab is in the
  // background; otherwise the on-page badge is enough.
  const isWindowFocusedRef = useRef(typeof document === "undefined" || !document.hidden);
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVis = () => { isWindowFocusedRef.current = !document.hidden; };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    if (user?.role !== "solicitor") return undefined;
    if (tab !== "messages") return undefined;
    // Initial pull immediately, then poll every 2 minutes while the tab
    // is open. Was 30s — too aggressive on prod where the underlying
    // aggregation walks every case every poll. 120s is plenty for a
    // human-watched feed and slashes Mongo load by 4x.
    fetchMessages();
    const id = setInterval(() => { fetchMessages(); }, 120000);
    return () => clearInterval(id);
  }, [user, tab, fetchMessages]);

  // Diff incoming messages against the previously-seen critical set and
  // fire a desktop notification (when permission granted) for every NEW
  // critical row. Updates the ref AFTER comparing so the very first poll
  // doesn't blast notifications for every existing critical case.
  const firstMessagesPollRef = useRef(true);
  useEffect(() => {
    const critical = [
      ...(messages.unread || []).filter((r) => r.band === "critical"),
      ...(messages.unreplied || []).filter((r) => r.band === "critical"),
    ];
    const currentIds = new Set(critical.map((r) => `${r.kind}:${r.item_id || r.case_id}`));
    if (firstMessagesPollRef.current) {
      // Seed the ref on first poll without firing — the user has presumably
      // already seen these on a previous session.
      knownCriticalIdsRef.current = currentIds;
      firstMessagesPollRef.current = false;
      return;
    }
    const fresh = critical.filter((r) => !knownCriticalIdsRef.current.has(`${r.kind}:${r.item_id || r.case_id}`));
    if (fresh.length > 0
        && typeof Notification !== "undefined"
        && Notification.permission === "granted"
        && !isWindowFocusedRef.current) {
      try {
        const first = fresh[0];
        const title = fresh.length === 1
          ? `Critical: ${first.case_title}`
          : `${fresh.length} cases just crossed 48 hrs`;
        const body = fresh.length === 1
          ? `${first.kind === "email" ? "Unreplied email" : "Unread message"} from ${first.author_name || "Unknown"}`
          : "Open ConveyTrack to triage.";
        const n = new Notification(title, {
          body,
          tag: "convey-critical-message",
          icon: "/favicon.ico",
        });
        n.onclick = () => {
          window.focus();
          if (fresh.length === 1 && first.case_id) {
            navigate(`/cases/${first.case_id}?tab=communications`);
          } else {
            navigate("/inbox");
          }
          n.close();
        };
      } catch (e) {  // noqa
        // Silent — desktop alerts are best-effort. Tab badge still updates.
      }
    }
    // In-page toast — fires whether the tab is focused or not, so a
    // solicitor sat on the Messages tab still gets a visible nudge
    // when a new case crosses critical.
    if (fresh.length > 0 && !firstMessagesPollRef.current && isWindowFocusedRef.current) {
      const first = fresh[0];
      const msg = fresh.length === 1
        ? `Critical: ${first.case_title} just crossed 48 hrs`
        : `${fresh.length} cases just crossed 48 hrs`;
      toast.error(msg, {
        duration: 8000,
        action: fresh.length === 1 && first.case_id ? {
          label: "Open",
          onClick: () => navigate(`/cases/${first.case_id}?tab=communications`),
        } : undefined,
      });
    }
    knownCriticalIdsRef.current = currentIds;
  }, [messages, navigate]);

  // Auto-poll while the cache warms up after a fresh Outlook reconnect.
  // We re-fetch every 5s; the warming flag clears itself when the backend
  // stops returning X-Inbox-Warming, ending the poll.
  useEffect(() => {
    if (!warming) return undefined;
    const id = setInterval(() => { fetchUnmatched(); }, 5000);
    return () => clearInterval(id);
  }, [warming, fetchUnmatched]);

  // Force-refresh triggers a Graph fetch + auto-match on the server.
  // Iteration 69: the endpoint is now non-blocking (returns immediately
  // as a background task) to prevent Cloudflare 520s on large mailboxes,
  // so we poll `/inbox/refresh/status` to know when it's done and only
  // re-pull the listing once the sync actually finishes.
  const handleHardRefresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.post("/inbox/refresh");
      if (data?.status === "already_running") {
        toast.info("Refresh already in progress — hang tight");
      } else {
        toast.info("Refreshing from Outlook…");
      }
      // Poll every 2s for up to 3 minutes. This is generous — a fresh
      // 90-day pull on a chatty mailbox with auto-match can take ~90s.
      const started = Date.now();
      const maxWait = 3 * 60 * 1000;
      let finalSummary = null;
      let idleStreak = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: st } = await API.get("/inbox/refresh/status");
        if (st.state === "done") { finalSummary = st.summary; break; }
        if (st.state === "error") {
          // Map machine error codes to human-friendly copy so the toast
          // doesn't read like a debug string (e.g. 'not_connected' →
          // "Your Outlook connection has expired — reconnect on Settings
          // → Integrations"). Iteration 70 review flagged this.
          const raw = st.error || "";
          const humanised =
            raw === "not_connected"
              ? "Your Outlook connection has expired — reconnect it on Settings → Integrations."
              : raw === "token_refresh_failed"
              ? "We couldn't refresh your Outlook token — try reconnecting under Settings → Integrations."
              : raw || "Refresh failed";
          throw new Error(humanised);
        }
        // Guard: if we see 'idle' or a missing state (lock doc was
        // purged mid-run) for two consecutive polls, treat as done —
        // otherwise we'd wait the full 3-minute timeout for nothing.
        if (!st.state || st.state === "idle") {
          idleStreak += 1;
          if (idleStreak >= 2) break;
        } else {
          idleStreak = 0;
        }
        if (Date.now() - started > maxWait) {
          throw new Error("Refresh is taking longer than expected — try again in a minute");
        }
      }
      await fetchUnmatched();
      const imported = finalSummary?.auto_match?.imported || 0;
      toast.success(
        imported > 0
          ? `Inbox refreshed — ${imported} new email(s) auto-attached`
          : "Inbox refreshed from Outlook"
      );
    } catch (err) {
      toast.error(err.message || formatApiErrorDetail(err.response?.data?.detail) || "Refresh failed");
    } finally {
      setLoading(false);
    }
  }, [fetchUnmatched]);

  // Admin-only: sweep every auto-matched email in case files under the
  // current (tightened) threading rules. Rows that no longer qualify are
  // unassigned and re-surface here in triage.
  const [rethreading, setRethreading] = useState(false);
  const handleRethread = useCallback(async () => {
    if (!window.confirm(
      "Re-run email threading?\n\n" +
      "This checks every auto-matched email in every case file against the current rules. " +
      "Emails that no longer match will be removed from their case and returned to Inbox triage.\n\n" +
      "This is safe to run multiple times."
    )) return;
    setRethreading(true);
    try {
      const { data } = await API.post("/admin/inbox/rethread");
      const kept = data?.kept ?? 0;
      const un = data?.unassigned ?? 0;
      await fetchUnmatched();
      toast.success(
        un > 0
          ? `Re-threading complete — ${un} email(s) unassigned, ${kept} kept in place.`
          : `Re-threading complete — ${kept} emails checked, none needed changes.`
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Re-threading failed");
    } finally {
      setRethreading(false);
    }
  }, [fetchUnmatched]);

  // Quick attach — single-click route from the candidate-case hint on
  // multi-match rows. Calls the same endpoint as the picker dialog but
  // skips the case search/select step.
  const handleQuickAttach = async (msg, caseId) => {
    try {
      await API.post(`/inbox/unmatched/${encodeURIComponent(msg.graph_message_id)}/attach`, { case_id: caseId });
      setItems((arr) => arr.filter((m) => m.graph_message_id !== msg.graph_message_id));
      toast.success("Email attached to case");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Attach failed");
    }
  };

  // Reply / Reply-all — fetches the full HTML body first (the listing
  // only ships bodyPreview to keep payloads small) so the quoted block
  // in the composer is accurate, then opens ComposeEmailDialog with no
  // caseId (it'll dispatch via /api/outlook/send instead of recording
  // against a case).
  const handleReply = async (msg, mode) => {
    let bodyHtml = "";
    try {
      const { data } = await API.get(`/inbox/unmatched/${encodeURIComponent(msg.graph_message_id)}/body`);
      bodyHtml = data.body_html || "";
    } catch {
      // fall back to preview if Graph fetch fails — the composer still works
    }
    setComposeReply({
      replyTo: {
        id: msg.graph_message_id,
        from_email: msg.from_email,
        from_name: msg.from_name,
        to_emails: msg.to_emails,
        cc_emails: msg.cc_emails,
        subject: msg.subject,
        body_html: bodyHtml,
        body_preview: msg.body_preview,
        sent_at: msg.sent_at,
      },
      mode,
    });
  };

  // Lazy-load the full HTML body when the user opens a message. The list
  // endpoint only ships `bodyPreview` to keep the payload small enough to
  // span the full 30-day window without timing out.
  useEffect(() => {
    if (!openMsg) return undefined;
    const targetId = openMsg.graph_message_id;
    // Skip refetch if we already have the body for this message (the user
    // re-opened the same row). We DON'T depend on openBody.id in the
    // hook deps because doing so triggers the effect cleanup which would
    // cancel the in-flight fetch every time we set state.
    let cancelled = false;
    setOpenBody((prev) => prev.id === targetId ? prev : { id: targetId, html: "", loading: true });
    setOpenAttachments((prev) => prev.id === targetId ? prev : { id: targetId, items: [], loading: !!openMsg.has_attachments });
    (async () => {
      try {
        const { data } = await API.get(`/inbox/unmatched/${encodeURIComponent(targetId)}/body`);
        if (!cancelled) setOpenBody({ id: targetId, html: data.body_html || "", loading: false });
      } catch {
        if (!cancelled) setOpenBody({ id: targetId, html: "", loading: false });
      }
      if (!openMsg.has_attachments) {
        if (!cancelled) setOpenAttachments({ id: targetId, items: [], loading: false });
        return;
      }
      try {
        const { data } = await API.get(`/inbox/unmatched/${encodeURIComponent(targetId)}/attachments`);
        if (!cancelled) {
          const real = (data.attachments || []).filter((a) => !a.is_inline);
          setOpenAttachments({ id: targetId, items: real, loading: false });
        }
      } catch {
        if (!cancelled) setOpenAttachments({ id: targetId, items: [], loading: false });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMsg]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="inbox-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Inbox triage
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {tab === "emails"
                ? "Outlook emails that don't match any case on file. Attach to a case, create a new case, or dismiss."
                : "Unread case messages and inbound emails on cases that nobody on the firm has replied to yet."}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => navigate("/inbox/archive")}
              className="text-xs px-3 py-1.5 rounded-md border border-[#100f5d]/30 bg-white text-[#100f5d] font-medium hover:bg-[#100f5d]/5 flex items-center gap-1.5"
              data-testid="open-compliance-archive">
              Compliance archive →
            </button>
            {tab === "emails" && (
              <>
                <Button onClick={handleHardRefresh} disabled={loading} variant="outline" className="gap-1.5" data-testid="inbox-refresh-btn">
                  <ArrowsClockwise weight="bold" className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
                {user?.is_admin && (
                  <Button
                    onClick={handleRethread}
                    disabled={rethreading}
                    variant="outline"
                    className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="inbox-rethread-btn"
                    title="Re-check every auto-matched email under the current threading rules and un-assign anything that no longer qualifies."
                  >
                    <ArrowsClockwise weight="bold" className={`w-4 h-4 ${rethreading ? "animate-spin" : ""}`} />
                    {rethreading ? "Re-threading…" : "Re-run threading"}
                  </Button>
                )}
              </>
            )}
            {tab === "messages" && (
              <>
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium" data-testid="inbox-messages-live-indicator" title="Auto-refreshing every 30 seconds">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  Live · refreshes every 30s
                </span>
                <Button onClick={fetchMessages} disabled={messagesLoading} variant="outline" className="gap-1.5" data-testid="inbox-messages-refresh">
                  <ArrowsClockwise weight="bold" className={`w-4 h-4 ${messagesLoading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Tab toggle — Emails (Outlook triage) vs Messages (unread/unreplied) */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div
            className="inline-flex bg-white border border-slate-200 rounded-lg p-1 shadow-sm"
            data-testid="inbox-tab-toggle"
          >
            <button
              type="button"
              onClick={() => setTab("emails")}
              data-testid="inbox-tab-emails"
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                tab === "emails" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <EnvelopeSimple weight="bold" className="w-4 h-4" />
              Emails
            </button>
            <button
              type="button"
              onClick={() => setTab("messages")}
              data-testid="inbox-tab-messages"
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                tab === "messages" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <BellRinging weight="bold" className="w-4 h-4" />
              Messages
              {messages.counts.total > 0 && (
                <span
                  className={`ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${
                    messages.counts.critical > 0
                      ? "bg-red-100 text-red-700"
                      : messages.counts.warning > 0
                      ? "bg-amber-100 text-amber-800"
                      : "bg-slate-100 text-slate-700"
                  }`}
                  data-testid="inbox-tab-messages-badge"
                >
                  {messages.counts.total}
                </span>
              )}
            </button>
          </div>

          {tab === "messages" && (
            <div className="inline-flex bg-white border border-slate-200 rounded-md p-0.5 h-8" data-testid="inbox-messages-scope">
              {[
                { value: "mine", label: "Mine" },
                { value: "all", label: "All firm" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setScope(opt.value)}
                  data-testid={`inbox-messages-scope-${opt.value}`}
                  className={`px-3 text-xs font-medium rounded transition-colors ${
                    scope === opt.value ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Desktop-alert opt-in — shown once per browser until granted or dismissed.
            Now visible on BOTH tabs so the user can enable from wherever they are. */}
        {notifPermission === "default" && !notifDismissed && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="inbox-notif-prompt">
            <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <BellRinging weight="bold" className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-[180px]">
              <p className="text-sm font-semibold text-slate-900">Get a desktop alert the moment a new client email lands</p>
              <p className="text-xs text-slate-600 mt-0.5">We'll ping you when an unmatched email arrives in your Outlook — even when this tab is in the background. Click the alert to jump straight here.</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button onClick={requestNotifPermission} size="sm" className="h-8 px-3 text-xs text-white" style={{ background: "#100f5d" }} data-testid="inbox-notif-enable">
                Enable alerts
              </Button>
              <Button onClick={dismissNotifPrompt} variant="ghost" size="sm" className="h-8 px-2 text-xs text-slate-500" data-testid="inbox-notif-dismiss">
                Not now
              </Button>
            </div>
          </div>
        )}

        {/* Filters — emails tab only */}
        {tab === "emails" && (
        <>
        <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="inbox-filters">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass weight="bold" className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search subject, sender name, sender email…"
              className="pl-9 h-9 text-sm"
              data-testid="inbox-search"
            />
          </div>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-36 h-9 text-xs gap-1.5" data-testid="inbox-days-select"><Calendar weight="bold" className="w-3.5 h-3.5" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
              <SelectItem value="365">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
          {/* Direction segmented filter — All / Inbox / Sent. */}
          <div className="inline-flex bg-white border border-slate-200 rounded-md p-0.5 h-9" data-testid="inbox-folder-filter">
            {[
              { value: "all", label: "All" },
              { value: "inbox", label: "Inbox" },
              { value: "sent", label: "Sent" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFolderFilter(opt.value)}
                data-testid={`inbox-folder-${opt.value}`}
                className={`px-3 text-xs font-medium rounded transition-colors ${
                  folderFilter === opt.value
                    ? "bg-slate-900 text-white"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
            <Checkbox checked={hasAttachments} onCheckedChange={setHasAttachments} data-testid="inbox-attachments-filter" />
            <Paperclip weight="bold" className="w-3.5 h-3.5 text-slate-400" />
            Has attachments
          </label>
        </div>

        {/* Reconnect banner — shown when Microsoft has revoked the
            user's refresh token (e.g. password change, admin sign-out,
            MFA reset). Without this the inbox just silently stops
            updating; we surface a clear call-to-action with a link to
            the integrations settings page. */}
        {outlookStatus?.needs_reconnect && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-3" data-testid="inbox-reconnect-banner">
            <Warning weight="fill" className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-900">Your Outlook connection has expired</p>
              <p className="text-xs text-red-700 mt-0.5">
                {outlookStatus.last_refresh_error === "invalid_grant"
                  ? "Microsoft revoked your sign-in (this usually happens after a password change, MFA reset, or an IT admin signing you out of all sessions)."
                  : "Microsoft is no longer accepting your stored sign-in."}
                {" "}Emails will not sync to your case files until you reconnect.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => navigate("/settings/integrations")}
              className="bg-red-600 hover:bg-red-700 text-white shrink-0"
              data-testid="inbox-reconnect-btn"
            >
              Reconnect Outlook
            </Button>
          </div>
        )}

        {/* Warming banner — shown right after a fresh Outlook (re)connect
            while the backend is streaming Graph into the cache. The page
            auto-polls every 5s; emails appear as they're written. */}
        {warming && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-3" data-testid="inbox-warming-banner">
            <ArrowsClockwise weight="bold" className="w-4 h-4 text-amber-600 mt-0.5 animate-spin shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900">Syncing your Outlook — first run</p>
              <p className="text-xs text-amber-700 mt-0.5">
                This is a one-off after you reconnect — we're pulling the last 45 days from Microsoft Graph. Emails will appear here as they come through (usually 20–30 seconds). You can leave this page; the sync continues in the background.
              </p>
            </div>
          </div>
        )}

        {/* List */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-sm text-slate-400">Loading…</div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center" data-testid="inbox-empty">
              <EnvelopeSimple weight="duotone" className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-500">No unmatched emails in this window.</p>
              <p className="text-xs text-slate-400 mt-1">Either everything has a case, or there's nothing in your Outlook in that range.</p>
            </div>
          ) : (
            <>
              {(() => {
                const pageItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
                const allChecked = pageItems.length > 0 && pageItems.every((m) => selEmails.has(m.graph_message_id));
                const someChecked = pageItems.some((m) => selEmails.has(m.graph_message_id));
                const toggleAll = () => setSelEmails((s) => {
                  const n = new Set(s);
                  if (allChecked) pageItems.forEach((m) => n.delete(m.graph_message_id));
                  else pageItems.forEach((m) => n.add(m.graph_message_id));
                  return n;
                });
                return (
                  <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60" data-testid="inbox-bulk-bar">
                    <Checkbox
                      checked={allChecked ? true : (someChecked ? "indeterminate" : false)}
                      onCheckedChange={toggleAll}
                      aria-label="Select all on page"
                      data-testid="inbox-select-all"
                    />
                    {selEmails.size > 0 ? (
                      <>
                        <span className="text-xs font-medium text-slate-700" data-testid="inbox-selected-count">
                          {selEmails.size} selected
                        </span>
                        <div className="ml-auto flex items-center gap-2">
                          <Button
                            variant="outline" size="sm" className="h-8 px-2.5 text-xs"
                            onClick={() => setSelEmails(new Set())}
                            data-testid="inbox-clear-selection"
                          >
                            Clear
                          </Button>
                          <Button
                            variant="outline" size="sm" className="h-8 px-2.5 text-xs gap-1.5 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            onClick={bulkDismiss}
                            data-testid="inbox-bulk-dismiss"
                          >
                            <X weight="bold" className="w-3.5 h-3.5" />
                            Dismiss
                          </Button>
                          <Button
                            variant="outline" size="sm" className="h-8 px-2.5 text-xs gap-1.5"
                            onClick={() => {
                              const chosen = items.filter((m) => selEmails.has(m.graph_message_id));
                              if (chosen.length) setAction({ msgs: chosen, mode: "newcase" });
                            }}
                            data-testid="inbox-bulk-newcase"
                          >
                            <FilePlus weight="bold" className="w-3.5 h-3.5" />
                            New case
                          </Button>
                          <Button
                            size="sm" className="h-8 px-3 text-xs gap-1.5 text-white" style={{ background: "#100f5d" }}
                            onClick={() => {
                              const chosen = items.filter((m) => selEmails.has(m.graph_message_id));
                              if (chosen.length) setAction({ msgs: chosen, mode: "attach" });
                            }}
                            data-testid="inbox-bulk-attach"
                          >
                            <ArrowSquareIn weight="bold" className="w-3.5 h-3.5" />
                            Add {selEmails.size} to file
                          </Button>
                        </div>
                      </>
                    ) : (
                      <span className="text-xs text-slate-500">Select emails to bulk-file, dismiss, or start a case — tip: hold Shift to select a range</span>
                    )}
                  </div>
                );
              })()}
            <ul className="divide-y divide-slate-100" data-testid="inbox-list">
              {items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((m, idx) => {
                const when = new Date(m.sent_at);
                const whenLabel = when.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
                const isSent = m.folder === "sent";
                const toLine = (m.to_emails || []).join(", ");
                return (
                  <li key={m.graph_message_id} className="p-4 hover:bg-slate-50/50" data-testid={`inbox-row-${m.graph_message_id}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selEmails.has(m.graph_message_id)}
                        onClick={(e) => handleRowSelect(e, idx, m.graph_message_id)}
                        className="mt-1 shrink-0"
                        aria-label="Select email"
                        data-testid={`inbox-select-${m.graph_message_id}`}
                      />
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          isSent ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"
                        }`}
                        data-testid={`inbox-row-direction-${m.graph_message_id}`}
                      >
                        {isSent ? <PaperPlaneTilt weight="bold" className="w-4 h-4" /> : <ArrowSquareIn weight="bold" className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-flex items-center text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
                              isSent ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"
                            }`}
                            data-testid={`inbox-row-folder-${m.graph_message_id}`}
                          >
                            {isSent ? "Sent" : "Inbox"}
                          </span>
                          <span className="text-[11px] text-slate-400">{whenLabel}</span>
                          {m.has_attachments && (
                            <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5"><Paperclip weight="bold" className="w-3 h-3" /></span>
                          )}
                        </div>
                        <button onClick={() => setOpenMsg(m)} className="block w-full text-left mt-0.5 group" data-testid={`inbox-open-${m.graph_message_id}`}>
                          <p className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 truncate">{m.subject}</p>
                          <p className="text-[11px] text-slate-500 truncate mt-0.5">
                            {isSent ? (
                              <>To <span className="font-medium text-slate-700">{toLine || "—"}</span></>
                            ) : (
                              <>From <span className="font-medium text-slate-700">{m.from_name || m.from_email}</span> &lt;{m.from_email}&gt;</>
                            )}
                          </p>
                          {m.body_preview && (
                            <p className="text-[11px] text-slate-400 mt-1 line-clamp-2">{m.body_preview}</p>
                          )}
                        </button>
                        {Array.isArray(m.candidate_cases) && m.candidate_cases.length > 1 && (
                          <div className="mt-2 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md" data-testid={`inbox-candidates-${m.graph_message_id}`}>
                            <span className="text-xs text-amber-800 shrink-0 mt-0.5 leading-tight">
                              <span className="font-semibold">{m.candidate_cases.length} candidate cases</span><span className="hidden sm:inline"> — pick one to file</span>
                            </span>
                            <div className="flex flex-wrap gap-1 flex-1">
                              {m.candidate_cases.slice(0, 5).map((c) => (
                                <button
                                  key={c.id}
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleQuickAttach(m, c.id); }}
                                  className="px-2 py-1 bg-white border border-amber-300 hover:border-amber-500 hover:bg-amber-50 rounded text-[11px] font-medium text-slate-700 max-w-[200px] truncate"
                                  title={c.property_address || c.title}
                                  data-testid={`inbox-candidate-${m.graph_message_id}-${c.id}`}
                                >
                                  {c.title || c.property_address || c.id.slice(0, 8)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* "Why not auto-matched?" hint. Persisted by
                            the auto-matcher onto the cache row when it
                            couldn't confidently attach — surfaces the
                            exact reason (ambiguous address, no ref-tag,
                            firm-internal only, etc.). Iteration 69. */}
                        {m.match_debug && !(Array.isArray(m.candidate_cases) && m.candidate_cases.length > 1) && (
                          <details
                            className="mt-2 text-[11px] text-slate-500"
                            data-testid={`inbox-match-debug-${m.graph_message_id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <summary className="cursor-pointer hover:text-slate-700 select-none">
                              Why not auto-matched?
                            </summary>
                            <div className="mt-1.5 p-2 bg-slate-50 border border-slate-200 rounded text-slate-600 leading-relaxed">
                              {m.match_debug.thread_miss && (
                                <div>• Reply headers: {m.match_debug.thread_miss}</div>
                              )}
                              {m.match_debug.ref_tag_miss && (
                                <div>• Subject ref tag: {m.match_debug.ref_tag_miss}</div>
                              )}
                              {m.match_debug.case_ref_miss && (
                                <div>• File reference: {m.match_debug.case_ref_miss}</div>
                              )}
                              {m.match_debug.email_miss && (
                                <div>• Address match: {m.match_debug.email_miss}</div>
                              )}
                              {Array.isArray(m.match_debug.external_participants) && m.match_debug.external_participants.length > 0 && (
                                <div className="mt-1 text-slate-400">
                                  External addresses seen: {m.match_debug.external_participants.slice(0, 4).join(", ")}
                                </div>
                              )}
                            </div>
                          </details>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" onClick={() => setAction({ msg: m, mode: "attach" })} data-testid={`inbox-attach-${m.graph_message_id}`}>
                          <LinkIcon weight="bold" className="w-3.5 h-3.5" /> Attach to case
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" onClick={() => setAction({ msg: m, mode: "newcase" })} data-testid={`inbox-newcase-${m.graph_message_id}`}>
                          <FilePlus weight="bold" className="w-3.5 h-3.5" /> New case
                        </Button>
                        <button
                          type="button"
                          onClick={() => handleReply(m, "reply")}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                          title="Reply"
                          data-testid={`inbox-reply-${m.graph_message_id}`}
                        >
                          <ArrowBendUpLeft weight="bold" className="w-3.5 h-3.5" />
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button type="button" className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900" title="More actions" data-testid={`inbox-more-${m.graph_message_id}`}>
                              <DotsThreeVertical weight="bold" className="w-3.5 h-3.5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem onClick={() => handleReply(m, "replyAll")} data-testid={`inbox-reply-all-${m.graph_message_id}`}>
                              <ArrowBendDoubleUpLeft weight="bold" className="w-3.5 h-3.5 mr-2" /> Reply all
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setConfirmDelete({ msg: m, scope: "local" })} data-testid={`inbox-delete-local-${m.graph_message_id}`} className="text-red-600 focus:text-red-700">
                              <X weight="bold" className="w-3.5 h-3.5 mr-2" /> Delete from ConveyTrack only
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete({ msg: m, scope: "outlook" })}
                          className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                          title="Delete (also moves to Outlook Deleted Items)"
                          data-testid={`inbox-delete-${m.graph_message_id}`}
                        >
                          <Trash weight="bold" className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            </>
          )}
          {/* Numbered pagination — only renders when there's more than one
              page worth of rows. Builds a sliding window of up to 5 numbers
              centred on the current page, with first/last shortcuts. */}
          {items.length > PAGE_SIZE && (() => {
            const totalPages = Math.ceil(items.length / PAGE_SIZE);
            const windowSize = 5;
            let start = Math.max(1, page - Math.floor(windowSize / 2));
            let end = Math.min(totalPages, start + windowSize - 1);
            start = Math.max(1, end - windowSize + 1);
            const pages = [];
            for (let i = start; i <= end; i += 1) pages.push(i);
            const startIdx = (page - 1) * PAGE_SIZE + 1;
            const endIdx = Math.min(page * PAGE_SIZE, items.length);
            const PageBtn = ({ value, label, disabled, active }) => (
              <button
                type="button"
                disabled={disabled}
                onClick={() => { setPage(value); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className={`min-w-[34px] h-8 px-2 text-xs font-medium rounded border ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100 disabled:opacity-40"
                }`}
                data-testid={`inbox-page-${value}`}
              >
                {label ?? value}
              </button>
            );
            return (
              <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between gap-3 flex-wrap bg-slate-50" data-testid="inbox-pager">
                <p className="text-xs text-slate-500">
                  Showing <span className="font-semibold text-slate-900">{startIdx}-{endIdx}</span> of {items.length} emails · Page {page} of {totalPages}
                </p>
                <div className="flex items-center gap-1">
                  <PageBtn value={1} label="«" disabled={page === 1} />
                  <PageBtn value={Math.max(1, page - 1)} label="‹" disabled={page === 1} />
                  {start > 1 && <span className="text-slate-400 text-xs px-1">…</span>}
                  {pages.map((p) => (
                    <PageBtn key={p} value={p} active={p === page} />
                  ))}
                  {end < totalPages && <span className="text-slate-400 text-xs px-1">…</span>}
                  <PageBtn value={Math.min(totalPages, page + 1)} label="›" disabled={page === totalPages} />
                  <PageBtn value={totalPages} label="»" disabled={page === totalPages} />
                </div>
              </div>
            );
          })()}
        </div>
        </>
        )}

        {/* Messages tab — unread case notes + unreplied inbound emails. */}
        {tab === "messages" && (
          <MessagesView
            data={messages}
            loading={messagesLoading}
            subTab={messagesSubTab}
            onSubTabChange={setMessagesSubTab}
            onOpenCase={(caseId, row) => {
              // Notes live on the Notes tab; emails on Communications.
              // Passing the item_id via ?highlight lets the receiving
              // tab scroll to and highlight the specific row so the
              // solicitor lands directly on the message that pinged
              // them, rather than a generic tab view.
              const kind = row?.kind || "email";
              const targetTab = kind === "note" ? "notes" : "communications";
              const highlight = row?.item_id ? `&highlight=${encodeURIComponent(row.item_id)}` : "";
              const chan = row?.channel && row.channel !== "email" ? `&channel=${encodeURIComponent(row.channel)}` : "";
              navigate(`/cases/${caseId}?tab=${targetTab}${highlight}${chan}`);
            }}
            busyRowId={busyRowId}
            onMarkRead={async (row) => {
              const rowKey = row.item_id || row.case_id;
              setBusyRowId(rowKey);
              // Optimistic — remove the row immediately so the user sees
              // instant feedback. We revert on error.
              setMessages((m) => ({
                ...m,
                unread: m.unread.filter((r) => r.item_id !== row.item_id),
              }));
              try {
                await API.post(`/inbox/messages/note/${row.item_id}/mark-seen`, null, {
                  params: { channel: row.channel || "client" },
                });
                toast.success("Marked as read", {
                  duration: 10000,
                  action: {
                    label: "Undo",
                    onClick: async () => {
                      try {
                        await API.post(`/inbox/messages/note/${row.item_id}/undo-seen`, null, {
                          params: { channel: row.channel || "client" },
                        });
                        fetchMessages();
                        toast.info("Restored to unread");
                      } catch (err) {
                        toast.error("Couldn't undo — please refresh");
                      }
                    },
                  },
                });
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to mark as read");
                fetchMessages();
              } finally {
                setBusyRowId(null);
              }
            }}
            onMarkReplied={async (row) => {
              const rowKey = row.item_id || row.case_id;
              setBusyRowId(rowKey);
              // Optimistic remove from BOTH unread + unreplied.
              setMessages((m) => ({
                ...m,
                unread: m.unread.filter((r) => r.item_id !== row.item_id),
                unreplied: m.unreplied.filter((r) => r.item_id !== row.item_id),
              }));
              // Route to the right endpoint based on row kind. Notes
              // (client/referrer channel) hit the notes endpoint;
              // emails hit the case-communications endpoint.
              const isNote = row.kind === "note";
              const url = isNote
                ? `/inbox/messages/note/${row.item_id}/mark-replied?channel=${encodeURIComponent(row.channel || "client")}`
                : `/inbox/messages/email/${row.item_id}/mark-replied`;
              try {
                await API.post(url);
                toast.success("Marked as replied", {
                  duration: 10000,
                });
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to mark as replied");
                fetchMessages();
              } finally {
                setBusyRowId(null);
              }
            }}
            onMarkNoReply={async (row) => {
              const rowKey = row.item_id || row.case_id;
              setBusyRowId(rowKey);
              setMessages((m) => ({
                ...m,
                unread: m.unread.filter((r) => r.item_id !== row.item_id),
                unreplied: m.unreplied.filter((r) => r.item_id !== row.item_id),
              }));
              const isNote = row.kind === "note";
              const url = isNote
                ? `/inbox/messages/note/${row.item_id}/no-reply-needed?channel=${encodeURIComponent(row.channel || "client")}`
                : `/inbox/messages/email/${row.item_id}/no-reply-needed`;
              try {
                await API.post(url);
                toast.success("Marked: no reply needed", {
                  duration: 10000,
                });
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to mark");
                fetchMessages();
              } finally {
                setBusyRowId(null);
              }
            }}
            onBulkTriage={async (rows, action) => {
              // Optimistic: clear all selected rows from both lists.
              const ids = new Set(rows.map((r) => r.item_id));
              setMessages((m) => ({
                ...m,
                unread: m.unread.filter((r) => !ids.has(r.item_id)),
                unreplied: m.unreplied.filter((r) => !ids.has(r.item_id)),
              }));
              try {
                const { data } = await API.post("/inbox/messages/bulk-mark-seen", {
                  items: rows.map((r) => ({
                    item_id: r.item_id,
                    kind: r.kind,
                    channel: r.channel,
                    action,
                  })),
                });
                const label = action === "replied" ? "replied" : action === "no_reply_needed" ? "no-reply-needed" : "read";
                toast.success(`Marked ${data.ok} message${data.ok === 1 ? "" : "s"} as ${label}${data.failed ? ` (${data.failed} failed)` : ""}`);
                fetchMessages();
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk action failed");
                fetchMessages();
              }
            }}
          />
        )}
      </main>

      {/* Read modal */}
      <Dialog open={!!openMsg} onOpenChange={(o) => !o && setOpenMsg(null)}>
        <DialogContent className="w-[96vw] sm:w-[92vw] max-w-[1400px] h-[92vh] max-h-[92vh] flex flex-col p-0 gap-0" data-testid="inbox-msg-dialog">
          {openMsg && (
            <>
              <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200 shrink-0">
                <DialogTitle className="text-lg sm:text-xl pr-8 break-words" style={{ fontFamily: "Outfit, sans-serif" }}>{openMsg.subject}</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4 space-y-3 text-sm">
                <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 text-xs min-w-0">
                  <span className="text-slate-500">From</span>
                  <span className="text-slate-900 break-all">{openMsg.from_name ? `${openMsg.from_name} <${openMsg.from_email}>` : openMsg.from_email}</span>
                  <span className="text-slate-500">To</span>
                  <span className="text-slate-900 break-all">{openMsg.to_emails?.join(", ")}</span>
                  <span className="text-slate-500">Date</span>
                  <span className="text-slate-900">{new Date(openMsg.sent_at).toLocaleString("en-GB")}</span>
                </div>
                <div
                  className="prose prose-sm max-w-none border border-slate-200 rounded-lg p-4 bg-white overflow-x-auto min-h-[80px] email-body-viewer"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: openBody.loading ? `<p class="text-slate-400 italic">Loading body…</p>` : (openBody.html || `<p class="text-slate-400 italic">${openMsg.body_preview || "No body content."}</p>`) }}
                />
                {/* Attachments — lazy-loaded after body. Each item has a
                    Download button that streams the binary through the
                    backend (Outlook-authenticated). */}
                {openMsg.has_attachments && (
                  <div className="border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid="inbox-detail-attachments">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1">
                      <Paperclip weight="bold" className="w-3 h-3" />
                      {openAttachments.loading
                        ? "Loading attachments…"
                        : `${openAttachments.items.length} attachment${openAttachments.items.length === 1 ? "" : "s"}`}
                    </p>
                    {!openAttachments.loading && openAttachments.items.length === 0 && (
                      <p className="text-[11px] text-slate-400 italic">No downloadable attachments.</p>
                    )}
                    {openAttachments.items.length > 0 && (
                      <ul className="space-y-1">
                        {openAttachments.items.map((a) => (
                          <li key={a.id} className="text-xs text-slate-700 flex items-center justify-between gap-3" data-testid={`inbox-attachment-${a.id}`}>
                            <span className="truncate">
                              <span>{a.filename}</span>
                              {a.size > 0 && (
                                <span className="text-slate-400 ml-1.5">{(a.size / 1024).toFixed(1)} KB</span>
                              )}
                            </span>
                            <button
                              type="button"
                              onClick={() => downloadAttachment(
                                `/inbox/unmatched/${encodeURIComponent(openMsg.graph_message_id)}/attachments/${encodeURIComponent(a.id)}/download`,
                                a.filename,
                              )}
                              className="text-blue-600 hover:underline font-medium shrink-0"
                              data-testid={`inbox-attachment-download-${a.id}`}
                            >
                              Download
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ActionDialog action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); setSelEmails(new Set()); fetchUnmatched(); }} />

      {/* Compose dialog for Reply / Reply-all (no caseId — dispatches via /api/outlook/send) */}
      <ComposeEmailDialog
        caseId={null}
        open={!!composeReply}
        onClose={() => setComposeReply(null)}
        onSent={() => { setComposeReply(null); }}
        replyTo={composeReply?.replyTo}
        mode={composeReply?.mode || "new"}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && !deleting && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="inbox-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
              {confirmDelete?.scope === "local" ? "Remove from ConveyTrack?" : "Delete this email?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.scope === "local" ? (
                <>This hides <span className="font-medium text-slate-900">{confirmDelete?.msg?.subject}</span> from your Inbox triage. The original stays in your Outlook untouched. You can find it again by clicking <span className="font-medium">Dismissed</span> (coming soon) or restoring it from Outlook.</>
              ) : (
                <>This will move <span className="font-medium text-slate-900">{confirmDelete?.msg?.subject}</span> to your Outlook Deleted Items folder and remove it from ConveyTrack. This cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={async () => {
                if (!confirmDelete) return;
                const { msg, scope } = confirmDelete;
                setDeleting(true);
                try {
                  if (scope === "local") {
                    await API.post(`/inbox/unmatched/${encodeURIComponent(msg.graph_message_id)}/dismiss`);
                    setItems((arr) => arr.filter((m) => m.graph_message_id !== msg.graph_message_id));
                    toast.success("Removed from ConveyTrack (your Outlook copy is untouched)");
                  } else {
                    const { data } = await API.post(`/inbox/unmatched/${encodeURIComponent(msg.graph_message_id)}/delete-outlook`);
                    setItems((arr) => arr.filter((m) => m.graph_message_id !== msg.graph_message_id));
                    if (data.deleted_from_outlook) {
                      toast.success("Email moved to your Outlook Deleted Items");
                    } else {
                      toast.success("Removed from ConveyTrack (Outlook delete failed — check Outlook)");
                    }
                  }
                  setConfirmDelete(null);
                } catch (err) {
                  toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
                } finally {
                  setDeleting(false);
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="inbox-delete-confirm-btn"
            >
              {deleting ? "Deleting…" : (confirmDelete?.scope === "local" ? "Remove" : "Delete email")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function ActionDialog({ action, onClose, onDone }) {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [caseSearch, setCaseSearch] = useState("");
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [newCaseTitle, setNewCaseTitle] = useState("");
  const [newCaseType, setNewCaseType] = useState("purchase");
  const [newCaseAddress, setNewCaseAddress] = useState("");
  const [saving, setSaving] = useState(false);
  // A bulk action carries `msgs`; a single action carries `msg`. Normalise.
  const msgs = action?.msgs || (action?.msg ? [action.msg] : []);
  const isBulk = msgs.length > 1;

  useEffect(() => {
    if (!action) return;
    setSelectedCaseId("");
    setCaseSearch("");
    setNewCaseTitle((action.msgs?.[0] || action.msg)?.subject?.slice(0, 100) || "");
    setNewCaseType("purchase");
    setNewCaseAddress("");
    if (action.mode === "attach") {
      // scope=all so a solicitor can attach to any firm case, not just
      // ones assigned to them (previously silently missing all
      // colleagues' cases from the picker).
      API.get("/cases", { params: { scope: "all" } })
        .then(({ data }) => setCases(Array.isArray(data) ? data : []))
        .catch(() => setCases([]));
    }
  }, [action]);

  const filteredCases = (() => {
    const q = caseSearch.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter((c) =>
      (c.title || "").toLowerCase().includes(q)
      || (c.property_address || "").toLowerCase().includes(q)
      || (c.case_ref || "").toLowerCase().includes(q)
      || (c.auto_ref || "").toLowerCase().includes(q)
      || (c.client_name || "").toLowerCase().includes(q)
      || (c.client_email || "").toLowerCase().includes(q)
    );
  })();

  const handleAttach = async () => {
    if (!selectedCaseId) { toast.error("Pick a case first"); return; }
    setSaving(true);
    try {
      if (isBulk) {
        const { data } = await API.post(`/inbox/unmatched/attach-bulk`, {
          case_id: selectedCaseId,
          graph_message_ids: msgs.map((m) => m.graph_message_id),
        });
        const parts = [];
        if (data.attached) parts.push(`${data.attached} added`);
        if (data.already_attached) parts.push(`${data.already_attached} already on file`);
        if (data.failed?.length) parts.push(`${data.failed.length} failed`);
        toast.success(`${data.case_title || "Case"} — ${parts.join(" · ") || "no change"}`);
      } else {
        await API.post(`/inbox/unmatched/${encodeURIComponent(msgs[0].graph_message_id)}/attach`, {
          case_id: selectedCaseId,
        });
        toast.success("Email attached to case");
      }
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Attach failed");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCase = async () => {
    if (!newCaseTitle.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const { data } = await API.post(`/inbox/unmatched/${encodeURIComponent(msgs[0].graph_message_id)}/create-case`, {
        title: newCaseTitle.trim(),
        case_type: newCaseType,
        property_address: newCaseAddress.trim(),
      });
      const caseId = data.case_id;
      // Attach the remaining selected emails to the freshly-created case.
      if (isBulk && msgs.length > 1 && caseId) {
        await API.post(`/inbox/unmatched/attach-bulk`, {
          case_id: caseId,
          graph_message_ids: msgs.slice(1).map((m) => m.graph_message_id),
        });
      }
      toast.success(isBulk ? `New case created — ${msgs.length} emails attached` : "New case created");
      onClose();
      navigate(`/cases/${caseId}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!action} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="inbox-action-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
            {action?.mode === "newcase"
              ? (isBulk ? `Create a new case from these ${msgs.length} emails` : "Create a new case from this email")
              : (isBulk ? `Attach ${msgs.length} emails to a case` : "Attach email to existing case")}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {action?.mode === "newcase"
              ? "Set the initial case details. You can adjust everything later on the case page."
              : "Choose which case this email belongs to. It'll be added to that case's Communications tab."}
          </DialogDescription>
        </DialogHeader>
        {action?.mode === "attach" ? (
          <div className="space-y-3">
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2 leading-relaxed">
              {isBulk ? (
                <>These <span className="font-medium">{msgs.length} emails</span> will be added to the chosen case's Communications tab.</>
              ) : (
                <>Email <span className="font-medium">{msgs[0]?.subject}</span> will be added to the chosen case's Communications tab.</>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Pick a case</Label>
              <Input
                value={caseSearch}
                onChange={(e) => setCaseSearch(e.target.value)}
                placeholder="Search by client, address, ref (case_ref or auto_ref), or title"
                className="h-9 text-sm"
                data-testid="inbox-case-search"
              />
              <ul className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto" data-testid="inbox-case-results">
                {filteredCases.length === 0 ? (
                  <li className="p-3 text-xs text-slate-400 text-center">No cases match.</li>
                ) : filteredCases.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => setSelectedCaseId(c.id)}
                      className={`w-full text-left p-2.5 border-l-2 transition-colors ${selectedCaseId === c.id ? "border-blue-600 bg-blue-50" : "border-transparent hover:bg-slate-50"}`}
                      data-testid={`inbox-case-option-${c.id}`}
                    >
                      <p className="text-sm font-medium text-slate-900 break-words">
                        {c.title}
                        {c.auto_ref ? <span className="ml-1 text-[11px] font-mono text-slate-400">[{c.auto_ref}]</span> : null}
                      </p>
                      <p className="text-[11px] text-slate-500 break-words leading-snug">
                        {[c.client_name, c.property_address].filter(Boolean).join(" · ") || "—"}
                        {" · "}{c.case_type} · {c.status}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2 leading-relaxed">
              {isBulk
                ? `We'll create a new case using these details and auto-attach all ${msgs.length} selected emails to it.`
                : "We'll create a new case using these details and auto-attach this email to it."}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Case title</Label>
              <Input value={newCaseTitle} onChange={(e) => setNewCaseTitle(e.target.value)} maxLength={120} data-testid="inbox-newcase-title" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Case type</Label>
              <Select value={newCaseType} onValueChange={setNewCaseType}>
                <SelectTrigger data-testid="inbox-newcase-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CASE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Property address</Label>
              <Input value={newCaseAddress} onChange={(e) => setNewCaseAddress(e.target.value)} placeholder="(optional)" data-testid="inbox-newcase-address" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          {action?.mode === "attach" ? (
            <Button onClick={handleAttach} disabled={saving || !selectedCaseId} className="text-white" style={{ background: "#100f5d" }} data-testid="inbox-attach-confirm">
              {saving ? "Attaching…" : "Attach to case"}
            </Button>
          ) : (
            <Button onClick={handleCreateCase} disabled={saving || !newCaseTitle.trim()} className="text-white" style={{ background: "#100f5d" }} data-testid="inbox-newcase-confirm">
              {saving ? "Creating…" : "Create case"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// MessagesView — companion feed inside the Inbox page. Renders unread case
// notes (client/referrer → solicitor with no solicitor `seen_by` entry) and
// unreplied inbound emails (latest case_communications row is incoming).
// Items are colour-banded by age: fresh <24h, warning 24-48h, critical >48h.
// ============================================================================
function MessagesView({ data, loading, subTab, onSubTabChange, onOpenCase, onMarkRead, onMarkReplied, onMarkNoReply, onBulkTriage, busyRowId }) {
  const { unread = [], unreplied = [], counts = {} } = data || {};
  const all = [...unread, ...unreplied].sort((a, b) => b.age_hours - a.age_hours);
  const list = subTab === "unread" ? unread : subTab === "unreplied" ? unreplied : all;

  // Bulk-select state — reset on every sub-tab change so we don't
  // accidentally trigger actions on rows the solicitor can no longer
  // see. `Set` keeps toggling O(1).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => { setSelectedIds(new Set()); }, [subTab]);

  const toggleRow = (row) => {
    const key = row.item_id || row.case_id;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === list.length) return new Set();
      return new Set(list.map((r) => r.item_id || r.case_id));
    });
  };
  const doBulk = async (action) => {
    const rows = list.filter((r) => selectedIds.has(r.item_id || r.case_id));
    if (rows.length === 0) return;
    setBulkBusy(true);
    try {
      await onBulkTriage(rows, action);
      setSelectedIds(new Set());
    } finally {
      setBulkBusy(false);
    }
  };

  const fmtAge = (h) => {
    if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
    if (h < 24) return `${Math.round(h)}h`;
    return `${Math.round(h / 24)}d`;
  };
  const bandStyles = {
    critical: {
      pill: "bg-red-100 text-red-800 border-red-200",
      stripe: "bg-red-500",
      label: "Over 48 hrs",
    },
    warning: {
      pill: "bg-amber-100 text-amber-800 border-amber-200",
      stripe: "bg-amber-500",
      label: "24-48 hrs",
    },
    fresh: {
      pill: "bg-emerald-100 text-emerald-800 border-emerald-200",
      stripe: "bg-emerald-500",
      label: "Under 24 hrs",
    },
  };

  return (
    <div className="space-y-4" data-testid="inbox-messages-view">
      {/* Stat cards — split by band so the user sees critical breach count first. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-red-200 rounded-lg p-4 flex items-center justify-between" data-testid="inbox-messages-stat-critical">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-red-700">Critical</p>
            <p className="text-xs text-slate-500 mt-0.5">Over 48 hrs</p>
          </div>
          <p className="text-3xl font-bold text-red-700" style={{ fontFamily: "Outfit, sans-serif" }}>{counts.critical || 0}</p>
        </div>
        <div className="bg-white border border-amber-200 rounded-lg p-4 flex items-center justify-between" data-testid="inbox-messages-stat-warning">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Warning</p>
            <p className="text-xs text-slate-500 mt-0.5">24-48 hrs</p>
          </div>
          <p className="text-3xl font-bold text-amber-700" style={{ fontFamily: "Outfit, sans-serif" }}>{counts.warning || 0}</p>
        </div>
        <div className="bg-white border border-emerald-200 rounded-lg p-4 flex items-center justify-between" data-testid="inbox-messages-stat-fresh">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Fresh</p>
            <p className="text-xs text-slate-500 mt-0.5">Under 24 hrs</p>
          </div>
          <p className="text-3xl font-bold text-emerald-700" style={{ fontFamily: "Outfit, sans-serif" }}>{counts.fresh || 0}</p>
        </div>
      </div>

      {/* Sub-tabs — All / Unread / Unreplied */}
      <div className="inline-flex bg-white border border-slate-200 rounded-md p-0.5 h-9" data-testid="inbox-messages-subtabs">
        {[
          { value: "all", label: "All", count: (counts.total || 0) },
          { value: "unread", label: "Unread", count: counts.unread || 0 },
          { value: "unreplied", label: "Unreplied", count: counts.unreplied || 0 },
        ].map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onSubTabChange(opt.value)}
            data-testid={`inbox-messages-subtab-${opt.value}`}
            className={`px-3 text-xs font-medium rounded transition-colors inline-flex items-center gap-1.5 ${
              subTab === opt.value ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            {opt.label}
            <span className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded text-[10px] font-semibold ${
              subTab === opt.value ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
            }`}>{opt.count}</span>
          </button>
        ))}
      </div>

      {/* Bulk action bar — reveals when 2+ rows are selected. Sticky-ish
          feel with amber accent so it clearly signals "you've built up
          a selection". Idempotent bulk endpoint hits the server once
          regardless of how many rows are ticked. */}
      {selectedIds.size >= 1 && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center justify-between flex-wrap gap-2"
          data-testid="inbox-messages-bulk-bar"
        >
          <p className="text-sm font-medium text-amber-900" data-testid="inbox-messages-bulk-count">
            {selectedIds.size} selected
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => doBulk("read")}
              disabled={bulkBusy}
              className="text-[11px] px-3 py-1 rounded border border-blue-200 bg-white text-blue-700 hover:bg-blue-50 disabled:opacity-50 font-medium"
              data-testid="inbox-messages-bulk-mark-read"
            >
              {bulkBusy ? "Working…" : `Mark ${selectedIds.size} read`}
            </button>
            <button
              type="button"
              onClick={() => doBulk("replied")}
              disabled={bulkBusy}
              className="text-[11px] px-3 py-1 rounded border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 font-medium"
              data-testid="inbox-messages-bulk-mark-replied"
            >
              Mark all replied
            </button>
            <button
              type="button"
              onClick={() => doBulk("no_reply_needed")}
              disabled={bulkBusy}
              className="text-[11px] px-3 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 font-medium"
              data-testid="inbox-messages-bulk-no-reply"
            >
              No reply needed
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkBusy}
              className="text-[11px] px-3 py-1 text-slate-500 hover:text-slate-900 hover:underline"
              data-testid="inbox-messages-bulk-clear"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-sm text-slate-400">Loading...</div>
        ) : list.length === 0 ? (
          <div className="py-12 text-center" data-testid="inbox-messages-empty">
            <EnvelopeSimple weight="duotone" className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">All caught up.</p>
            <p className="text-xs text-slate-400 mt-1">No unread messages or unreplied emails on this scope.</p>
          </div>
        ) : (
          <>
            {/* Select-all header row */}
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center gap-3">
              <input
                type="checkbox"
                checked={selectedIds.size === list.length && list.length > 0}
                onChange={toggleAll}
                className="h-3.5 w-3.5 rounded border-slate-300 cursor-pointer"
                data-testid="inbox-messages-select-all"
                aria-label="Select all messages"
              />
              <span className="text-[11px] text-slate-500">
                {selectedIds.size > 0
                  ? `${selectedIds.size} of ${list.length} selected`
                  : `${list.length} message${list.length === 1 ? "" : "s"}`}
              </span>
            </div>
            <ul className="divide-y divide-slate-100">
            {list.map((row) => {
              const styles = bandStyles[row.band] || bandStyles.fresh;
              const isEmail = row.kind === "email";
              return (
                <li
                  key={`${row.kind}-${row.item_id || row.case_id}`}
                  className="relative pl-4 pr-4 py-3 hover:bg-slate-50 cursor-pointer"
                  onClick={() => onOpenCase(row.case_id, row)}
                  data-testid={`inbox-messages-row-${row.item_id || row.case_id}`}
                >
                  <span className={`absolute left-0 top-2 bottom-2 w-1 rounded-r ${styles.stripe}`} />
                  <div className="flex items-start gap-3 flex-wrap">
                    {/* Per-row select — needed for bulk triage */}
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.item_id || row.case_id)}
                      onChange={() => toggleRow(row)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 h-3.5 w-3.5 rounded border-slate-300 shrink-0 cursor-pointer"
                      data-testid={`inbox-messages-select-${row.item_id || row.case_id}`}
                      aria-label="Select this message"
                    />
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded ${
                          isEmail ? "bg-blue-50 text-blue-700" : row.channel === "referrer" ? "bg-purple-50 text-purple-700" : "bg-slate-100 text-slate-700"
                        }`}>
                          {isEmail ? "Email" : row.channel === "referrer" ? "Referrer msg" : "Client msg"}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full border ${styles.pill}`}>
                          {fmtAge(row.age_hours)} · {styles.label}
                        </span>
                        {row.case_ref && (
                          <span className="text-[10px] font-mono text-slate-400">[Ref: {row.case_ref}]</span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-slate-900 truncate mt-1">
                        {row.case_title}
                        {row.property_address && (
                          <span className="font-normal text-slate-500"> · {row.property_address}</span>
                        )}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                        From <span className="font-medium text-slate-700">{row.author_name || "Unknown"}</span>
                        {isEmail && row.subject && (
                          <span className="text-slate-500"> · {row.subject}</span>
                        )}
                      </p>
                      {row.snippet && (
                        <p className="text-[11px] text-slate-400 mt-1 line-clamp-2">{row.snippet}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                      <p className="text-[11px] text-slate-400">
                        {row.created_at ? new Date(row.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-"}
                      </p>
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        {isEmail ? (
                          <>
                            {/* Primary email action stays inline — the
                                one thing solicitors do 95% of the time.
                                Secondary triage moves into the ⋯ menu
                                so the row stays scannable on narrow
                                screens. */}
                            <button
                              type="button"
                              disabled={busyRowId === (row.item_id || row.case_id)}
                              onClick={(e) => { e.stopPropagation(); onMarkReplied(row); }}
                              className="text-[11px] px-2 py-0.5 rounded border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                              data-testid={`inbox-messages-mark-replied-${row.item_id}`}
                            >
                              Mark replied
                            </button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="text-slate-400 hover:text-slate-700 h-6 w-6 rounded hover:bg-slate-100 flex items-center justify-center"
                                  data-testid={`inbox-messages-more-${row.item_id}`}
                                  aria-label="More actions"
                                >
                                  <DotsThreeVertical weight="bold" className="w-3.5 h-3.5" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); onMarkNoReply(row); }}
                                  data-testid={`inbox-messages-mark-no-reply-${row.item_id}`}
                                >
                                  No reply needed
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </>
                        ) : (
                          <>
                            {/* Notes: Mark read is the fast-hit primary
                                action (see the message, acknowledge,
                                move on). Reply + No-reply-needed sit
                                behind ⋯ for the less common cases where
                                the solicitor followed up out-of-band or
                                nothing was needed. */}
                            <button
                              type="button"
                              disabled={busyRowId === (row.item_id || row.case_id)}
                              onClick={(e) => { e.stopPropagation(); onMarkRead(row); }}
                              className="text-[11px] px-2 py-0.5 rounded border border-blue-200 bg-white text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                              data-testid={`inbox-messages-mark-read-${row.item_id}`}
                              title="Mark this message as read — removes it from Unread but keeps it in Unreplied until you post a response."
                            >
                              Mark read
                            </button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="text-slate-400 hover:text-slate-700 h-6 w-6 rounded hover:bg-slate-100 flex items-center justify-center"
                                  data-testid={`inbox-messages-more-${row.item_id}`}
                                  aria-label="More actions"
                                >
                                  <DotsThreeVertical weight="bold" className="w-3.5 h-3.5" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); onMarkReplied(row); }}
                                  data-testid={`inbox-messages-mark-replied-${row.item_id}`}
                                >
                                  Mark replied
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); onMarkNoReply(row); }}
                                  data-testid={`inbox-messages-mark-no-reply-${row.item_id}`}
                                >
                                  No reply needed
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </>
                        )}
                        <span className="text-[11px] text-blue-600 hover:underline">Open →</span>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          </>
        )}
      </div>
    </div>
  );
}

